import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  CaretLeft,
  CaretRight,
  CheckCircle,
  CurrencyInr,
  Handshake,
  ChatCircleDots,
  UploadSimple,
  ShieldCheck,
  Clock,
  MapPin,
  Sparkle,
  Lightbulb,
  Cube,
  CalendarCheck,
  Question
} from '@phosphor-icons/react';
import { internshipBySlug, internshipCover, internshipCategoryLabel, DEFAULT_INTERNSHIP_PLANS } from '../../data/internships';
import { internshipAPI } from '../../services/api';
import DemoCertificate from './DemoCertificate';
import { applyPageSeo, pageUrl, SITE_URL } from '../../utils/seo';

const terms = [
  'Anyone can enrol. This is a paid internship with Growmore IT Services, Satara. You get an offer letter after payment and a completion letter after your last task is approved.',
  'Choose a plan on this page. The duration and fee you pay stay with your enrolment.',
  'Weekly tasks are small and simple. Every week a new short brief opens in your intern portal. The team reviews that week’s work in the same week.',
  'On the last week, your tasks are reviewed together. When the last task is approved, the completion letter is issued with a unique ID and QR.',
  'After payment, sign in with the email and password sent to you (person icon in the navbar).',
  'If a task feels unclear, message the team — we help you finish.',
  'Official letters stay on the Certificates page. The sample letters below are only a preview.'
];

const InternshipRolePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const role = useMemo(() => internshipBySlug(slug), [slug]);
  const [plans, setPlans] = useState(DEFAULT_INTERNSHIP_PLANS);
  const [selected, setSelected] = useState(DEFAULT_INTERNSHIP_PLANS[0]?.id);
  const [letterKind, setLetterKind] = useState('offer');
  const [formats, setFormats] = useState(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
    internshipAPI.catalog()
      .then(({ data }) => {
        const next = data.plans?.length ? data.plans : DEFAULT_INTERNSHIP_PLANS;
        setPlans(next);
        setSelected(next[0]?.id);
        if (data.formats) {
          setFormats(data.formats);
        }
      })
      .catch(() => {});
    if (!role) {
      return applyPageSeo({
        title: 'Internship | Growmore IT Services',
        description: 'Paid internships in Satara with weekly live tasks and official letters.',
        path: '/internship'
      });
    }
    return applyPageSeo({
      title: role.seoTitle,
      description: role.seoDescription,
      keywords: role.seoKeywords,
      path: `/internship/${role.slug}`,
      image: internshipCover(role.slug),
      type: 'article'
    });
  }, [role]);

  if (!role) {
    return (
      <div className="pt-32 pb-20 text-center min-h-screen bg-gradient-to-br from-slate-50 via-white to-sky-50 dark:from-dark-900 dark:via-dark-900 dark:to-dark-800">
        <p className="text-gray-500 dark:text-gray-400 mb-4">This internship was not found.</p>
        <Link to="/internship" className="text-brand-600 dark:text-brand-400 font-semibold">Back to internships</Link>
      </div>
    );
  }

  const cover = internshipCover(role.slug);
  const plan = plans.find((item) => String(item.id) === String(selected)) || plans[0];
  const url = pageUrl(`/internship/${role.slug}`);
  const faqs = role.faqs || [];
  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Course',
        name: role.headline || role.title,
        description: role.seoDescription || role.summary,
        url,
        provider: {
          '@type': 'Organization',
          name: 'Growmore IT Services',
          url: `${SITE_URL}/`
        },
        educationalLevel: 'Internship',
        occupationalCredentialAwarded: 'Offer letter and completion letter',
        areaServed: ['Satara', 'Maharashtra', 'India']
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
          { '@type': 'ListItem', position: 2, name: 'Internship', item: `${SITE_URL}/internship` },
          { '@type': 'ListItem', position: 3, name: role.title, item: url }
        ]
      },
      faqs.length
        ? {
            '@type': 'FAQPage',
            mainEntity: faqs.map((item) => ({
              '@type': 'Question',
              name: item.q,
              acceptedAnswer: { '@type': 'Answer', text: item.a }
            }))
          }
        : null
    ].filter(Boolean)
  };

  const apply = () => {
    if (!plan) return;
    navigate(`/internship/apply/${role.slug}?plan=${plan.id}`);
  };

  const PlanCard = ({ compact = false }) => (
    <div className={`rounded-2xl sm:rounded-3xl bg-white dark:bg-dark-800 border border-gray-100 dark:border-gray-800 shadow-medium min-w-0 ${compact ? 'p-3.5' : 'p-5'}`}>
      <p className="text-xs font-bold uppercase tracking-wide text-brand-600 dark:text-brand-400 mb-1.5">Plans & pricing</p>
      <h2 className="font-display text-base sm:text-lg font-extrabold text-gray-900 dark:text-white mb-3">Pick a duration</h2>
      <div className="space-y-2 mb-4">
        {plans.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setSelected(item.id)}
            className={`w-full text-left rounded-2xl border px-4 py-3 smooth-transition ${
              String(selected) === String(item.id)
                ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/25 dark:border-brand-500 shadow-sm'
                : 'border-gray-100 dark:border-gray-700 hover:border-brand-200 dark:hover:border-brand-700'
            }`}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="font-semibold text-sm text-gray-900 dark:text-white">{item.name}</span>
              <span className="inline-flex items-center text-brand-700 dark:text-brand-300 font-extrabold text-sm">
                <CurrencyInr className="w-3.5 h-3.5" /> {Number(item.price).toLocaleString('en-IN')}
              </span>
            </div>
            <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1 inline-flex items-center gap-1">
              <Clock className="w-3 h-3" /> {item.weeks} weeks
            </p>
            {item.description && <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">{item.description}</p>}
          </button>
        ))}
      </div>
      <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-4 inline-flex items-center gap-1">
        <MapPin className="w-3 h-3" /> Satara · offer + completion letters
      </p>
      <button type="button" onClick={apply} className="btn-primary text-white w-full py-3 inline-flex items-center justify-center gap-2">
        Apply this plan <CaretRight className="w-4 h-4" />
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-sky-50 dark:from-dark-900 dark:via-dark-900 dark:to-dark-800">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <div className="relative overflow-hidden min-h-[240px] sm:min-h-[280px]">
        <img src={cover} alt={`${role.headline || role.title} — Growmore IT Services Satara`} className="absolute inset-0 w-full h-full object-cover scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/55 to-slate-900/20" />
        <div className="relative section-max-width px-4 md:px-6 pt-20 sm:pt-24 pb-5 sm:pb-10 min-h-[240px] sm:min-h-[280px] flex flex-col justify-end">
          <nav aria-label="Breadcrumb" className="mb-2 sm:mb-3">
            <ol className="flex flex-wrap items-center gap-1 text-[11px] sm:text-xs font-semibold text-white/75">
              <li><Link to="/" className="hover:text-white">Home</Link></li>
              <li aria-hidden="true"><CaretRight className="w-3 h-3" weight="bold" /></li>
              <li><Link to="/internship" className="hover:text-white">Internship</Link></li>
              <li aria-hidden="true"><CaretRight className="w-3 h-3" weight="bold" /></li>
              <li className="text-white">{role.title}</li>
            </ol>
          </nav>
          <button type="button" onClick={() => navigate('/internship')} className="text-white/80 text-sm font-semibold inline-flex items-center gap-1 mb-2 sm:mb-4 w-fit">
            <CaretLeft className="w-4 h-4" /> All internships
          </button>
          <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-sky-200 mb-1.5">{internshipCategoryLabel(role.category)} intern</p>
          <h1 className="font-display text-[1.45rem] sm:text-5xl font-extrabold text-white max-w-3xl leading-snug break-words">{role.title}</h1>
          <p className="mt-2 sm:mt-3 text-sm sm:text-base text-white/80 max-w-2xl leading-6 sm:leading-7">{role.summary}</p>
        </div>
      </div>

      <div className="section-max-width px-4 md:px-6 py-5 sm:py-10 space-y-5 sm:space-y-10">
        <div className="lg:hidden">
          <PlanCard compact />
        </div>

        <div className="grid lg:grid-cols-3 gap-5 lg:gap-8">
          <div className="lg:col-span-2 space-y-5 sm:space-y-8 min-w-0">
            <section className="rounded-2xl sm:rounded-3xl bg-white dark:bg-dark-800 border border-gray-100 dark:border-gray-800 p-4 sm:p-6 shadow-soft min-w-0">
              <h2 className="font-display text-lg sm:text-xl font-extrabold text-gray-900 dark:text-white mb-2 sm:mb-3">About this track</h2>
              <p className="text-sm text-gray-600 dark:text-gray-300 leading-6 sm:leading-7 mb-5">{role.about}</p>

              <h3 className="font-display text-sm font-bold text-gray-900 dark:text-white mb-3 inline-flex items-center gap-2">
                <Sparkle className="w-4 h-4 text-brand-600 dark:text-brand-400" /> What you will learn
              </h3>
              <ol className="grid sm:grid-cols-2 gap-2 mb-7">
                {role.learn.map((item, i) => (
                  <li key={item} className="flex gap-2.5 items-start rounded-xl bg-slate-50 dark:bg-white/5 px-3 py-2.5">
                    <span className="mt-0.5 w-5 h-5 rounded-md bg-brand-50 dark:bg-brand-900/40 text-brand-700 dark:text-brand-300 text-[10px] font-bold flex items-center justify-center shrink-0">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <span className="text-[13px] text-gray-700 dark:text-gray-300 leading-5 break-words min-w-0">{item}</span>
                  </li>
                ))}
              </ol>

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <h3 className="font-display text-sm font-bold text-gray-900 dark:text-white mb-3 inline-flex items-center gap-2">
                    <Lightbulb className="w-4 h-4 text-amber-500" weight="fill" /> Real problems you will solve
                  </h3>
                  <ul className="space-y-2">
                    {(role.problems || []).map((item) => (
                      <li key={item} className="flex gap-2 items-start text-[13px] text-gray-600 dark:text-gray-300 leading-6 min-w-0">
                        <CheckCircle className="w-4 h-4 text-brand-600 dark:text-brand-400 shrink-0 mt-0.5" weight="fill" />
                        <span className="break-words min-w-0">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="font-display text-sm font-bold text-gray-900 dark:text-white mb-3 inline-flex items-center gap-2">
                    <Cube className="w-4 h-4 text-violet-500" weight="duotone" /> Features you will build
                  </h3>
                  <ul className="space-y-2">
                    {(role.features || []).map((item) => (
                      <li key={item} className="flex gap-2 items-start text-[13px] text-gray-600 dark:text-gray-300 leading-6 min-w-0">
                        <CheckCircle className="w-4 h-4 text-brand-600 dark:text-brand-400 shrink-0 mt-0.5" weight="fill" />
                        <span className="break-words min-w-0">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <p className="mt-6 text-sm text-gray-600 dark:text-gray-300 leading-6 rounded-2xl bg-brand-50/70 dark:bg-brand-900/20 border border-brand-100 dark:border-brand-800 px-4 py-3">
                Open for everyone. Weekly tasks are small and simple — one short brief each week. Pick a plan and start.
              </p>
              <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">You’ll use: {role.tools.join(' · ')}</p>
            </section>

            {faqs.length > 0 && (
              <section className="rounded-2xl sm:rounded-3xl bg-white dark:bg-dark-800 border border-gray-100 dark:border-gray-800 p-4 sm:p-6 shadow-soft min-w-0">
                <h2 className="font-display text-lg sm:text-xl font-extrabold text-gray-900 dark:text-white mb-4">
                  {role.title} FAQs
                </h2>
                <dl className="space-y-3">
                  {faqs.map((item) => (
                    <div key={item.q} className="rounded-xl border border-gray-100 dark:border-gray-700 px-3.5 py-3">
                      <dt className="flex items-start gap-2 font-display font-extrabold text-sm text-gray-900 dark:text-white">
                        <Question className="w-4 h-4 mt-0.5 shrink-0 text-brand-600" weight="fill" />
                        <span>{item.q}</span>
                      </dt>
                      <dd className="mt-1.5 pl-6 text-sm text-gray-600 dark:text-gray-400 leading-relaxed">{item.a}</dd>
                    </div>
                  ))}
                </dl>
              </section>
            )}

            <section className="rounded-2xl sm:rounded-3xl bg-white dark:bg-dark-800 border border-gray-100 dark:border-gray-800 p-4 sm:p-6 shadow-soft min-w-0">
              <h2 className="font-display text-lg sm:text-xl font-extrabold text-gray-900 dark:text-white mb-1 inline-flex items-center gap-2">
                <CalendarCheck className="w-5 h-5 text-brand-600 dark:text-brand-400" /> How weekly tasks work
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">After payment, open Intern login from the person icon in the navbar. Tasks stay small so you can finish them in the week.</p>
              <ol className="space-y-3 text-sm text-gray-600 dark:text-gray-300 leading-6">
                <li><strong className="text-gray-900 dark:text-white">1. New small task every week.</strong> A short brief opens in your portal — one focused piece of work, not a huge project.</li>
                <li><strong className="text-gray-900 dark:text-white">2. You submit that week.</strong> Write a few lines on what you did and attach 1–2 screenshots or a PDF.</li>
                <li><strong className="text-gray-900 dark:text-white">3. Same-week review.</strong> The team reviews that week’s task in that week and shares simple feedback.</li>
                <li><strong className="text-gray-900 dark:text-white">4. Last week.</strong> Your final small task is submitted and the weeks are reviewed together.</li>
                <li><strong className="text-gray-900 dark:text-white">5. Completion letter.</strong> When the last task is approved, the completion letter is emailed with QR verify.</li>
              </ol>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-4 inline-flex items-start gap-1.5">
                <UploadSimple className="w-4 h-4 shrink-0 mt-0.5" />
                You can always message the team if a brief needs a little extra time. We want you to finish well.
              </p>
            </section>

            <section className="rounded-2xl sm:rounded-3xl bg-gradient-to-br from-sky-50 to-white dark:from-dark-800 dark:to-dark-900 border border-sky-100 dark:border-gray-800 p-4 sm:p-6 shadow-soft min-w-0">
              <h2 className="font-display text-lg sm:text-xl font-extrabold text-gray-900 dark:text-white mb-2 inline-flex items-center gap-2">
                <ChatCircleDots className="w-5 h-5 text-brand-600 dark:text-brand-400" weight="fill" /> Stuck on a task?
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-300 leading-7 mb-4">
                Read the brief again, split it into small steps, and submit when you are ready. You can also ask the Growmore team — we will walk you through it.
              </p>
              <div className="flex flex-col sm:flex-row flex-wrap gap-3">
                <a href="https://wa.me/917350695792" target="_blank" rel="noreferrer" className="btn-primary text-white inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm">
                  <ChatCircleDots className="w-4 h-4" weight="fill" /> WhatsApp the team
                </a>
                <a href="mailto:info@growmoreitservices.com" className="btn-secondary inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm">
                  Email the team
                </a>
              </div>
            </section>

            <section className="rounded-2xl sm:rounded-3xl bg-white dark:bg-dark-800 border border-gray-100 dark:border-gray-800 p-4 sm:p-6 shadow-soft min-w-0">
              <h2 className="font-display text-lg sm:text-xl font-extrabold text-gray-900 dark:text-white mb-3 inline-flex items-center gap-2">
                <Handshake className="w-5 h-5 text-brand-600 dark:text-brand-400" /> Terms & conditions
              </h2>
              <ul className="space-y-2.5">
                {terms.map((item) => (
                  <li key={item} className="flex gap-2 items-start text-[13px] text-gray-600 dark:text-gray-300 leading-6 min-w-0">
                    <ShieldCheck className="w-4 h-4 text-brand-600 dark:text-brand-400 shrink-0 mt-0.5" weight="fill" />
                    <span className="break-words min-w-0">{item}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section className="min-w-0">
              <h2 className="font-display text-lg sm:text-xl font-extrabold text-gray-900 dark:text-white mb-2">Sample official letters</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 leading-6">
                Preview of the offer letter issued after enrolment and the completion letter issued after the last task is approved. Samples cannot be downloaded.
              </p>
              <div className="flex p-1 rounded-2xl bg-slate-100 dark:bg-white/10 mb-4 sm:mb-5 w-full sm:max-w-md">
                {[
                  { id: 'offer', label: 'Offer' },
                  { id: 'completion', label: 'Completion' }
                ].map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setLetterKind(item.id)}
                    className={`flex-1 py-2.5 rounded-xl text-sm font-semibold smooth-transition ${
                      letterKind === item.id
                        ? 'bg-white dark:bg-dark-800 text-brand-700 dark:text-brand-300 shadow-sm'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-white'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
              <DemoCertificate type={letterKind} roleTitle={role.title} formats={formats} />
            </section>
          </div>

          <aside className="hidden lg:block space-y-4 lg:sticky lg:top-28 h-fit">
            <PlanCard />
          </aside>
        </div>
      </div>
    </div>
  );
};

export default InternshipRolePage;
